import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  CreditCard, 
  Plus, 
  Edit, 
  Trash2, 
  DollarSign, 
  Calendar, 
  MessageSquare, 
  TrendingDown,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  X,
  Eye
} from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import WorkoutDetailModal from './WorkoutDetailModal';
import { buildApiUrl } from '../config/api';
import { getApiAccessToken } from '../config/authToken';
import {
  getServerTime,
  getWorkouts,
  createPayment,
  updatePayment,
  deletePayment,
} from '../services/api';
import { getNextDebitTime } from '../utils/workoutDebit';
import { useI18n } from '../i18n/context';
import { getIntlLocaleTag } from '../lib/localeRuntime';

// Currency codes in alphabetical order
const CURRENCIES = [
  'AED', 'AFN', 'ALL', 'AMD', 'ANG', 'AOA', 'ARS', 'AUD', 'AWG', 'AZN',
  'BAM', 'BBD', 'BDT', 'BGN', 'BHD', 'BIF', 'BMD', 'BND', 'BOB', 'BRL', 'BSD', 'BTN', 'BWP', 'BYN', 'BZD',
  'CAD', 'CDF', 'CHF', 'CLP', 'CNY', 'COP', 'CRC', 'CUP', 'CVE', 'CZK',
  'DJF', 'DKK', 'DOP', 'DZD',
  'EGP', 'ERN', 'ETB', 'EUR',
  'FJD', 'FKP',
  'GBP', 'GEL', 'GHS', 'GIP', 'GMD', 'GNF', 'GTQ', 'GYD',
  'HKD', 'HNL', 'HRK', 'HTG', 'HUF',
  'IDR', 'ILS', 'INR', 'IQD', 'IRR', 'ISK',
  'JMD', 'JOD', 'JPY',
  'KES', 'KGS', 'KHR', 'KMF', 'KPW', 'KRW', 'KWD', 'KYD', 'KZT',
  'LAK', 'LBP', 'LKR', 'LRD', 'LSL', 'LYD',
  'MAD', 'MDL', 'MGA', 'MKD', 'MMK', 'MNT', 'MOP', 'MRU', 'MUR', 'MVR', 'MWK', 'MXN', 'MYR', 'MZN',
  'NAD', 'NGN', 'NIO', 'NOK', 'NPR', 'NZD',
  'OMR',
  'PAB', 'PEN', 'PGK', 'PHP', 'PKR', 'PLN', 'PYG',
  'QAR',
  'RON', 'RSD', 'RUB', 'RWF',
  'SAR', 'SBD', 'SCR', 'SDG', 'SEK', 'SGD', 'SHP', 'SLE', 'SLL', 'SOS', 'SRD', 'STN', 'SYP', 'SZL',
  'THB', 'TJS', 'TMT', 'TND', 'TOP', 'TRY', 'TTD', 'TVD', 'TWD', 'TZS',
  'UAH', 'UGX', 'USD', 'UYU', 'UZS',
  'VES', 'VND', 'VUV',
  'WST',
  'XAF', 'XCD', 'XDR', 'XOF', 'XPF',
  'YER',
  'ZAR', 'ZMW', 'ZWL'
];

/** USD, EUR, затем валюты, которые тренер когда-либо использовал в оплатах (все клиенты), затем из истории текущего клиента, плюс текущее значение формы. */
function buildQuickCurrencyList(trainerCurrencies, clientUsedCurrencies, currentCurrency) {
  const quick = [];
  const seen = new Set();
  const push = (c) => {
    if (!c || seen.has(c)) return;
    seen.add(c);
    quick.push(c);
  };
  push('USD');
  push('EUR');
  for (const c of [...trainerCurrencies].sort()) {
    push(c);
  }
  for (const c of [...clientUsedCurrencies].sort()) {
    push(c);
  }
  push(currentCurrency);
  return quick;
}

/** TabBar с z-[200] выше модалки клиента (z-[90]); список валют не перекрывает таббар — клик по зоне вкладок срабатывал как смена вкладки. Перехватываем только полосу над TabBar (не весь экран, чтобы не блокировать шапку/кнопку «Назад» модалки). */
function SelectTabBarBlockerBackdrop({ open, onClose }) {
  if (!open || typeof document === 'undefined') return null;
  return createPortal(
    <div
      className="pointer-events-auto fixed bottom-0 left-0 right-0 z-[205] bg-transparent"
      style={{
        height: 'var(--app-tab-bar-height, 72px)',
      }}
      aria-hidden
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }}
    />,
    document.body
  );
}

const PaymentsTab = ({
  clientId,
  clientName,
  trainerId,
  openAddPaymentOnMount = false,
  mode = 'full', // 'full' | 'add_only'
  onRequestClose,
  onPaymentAdded,
}) => {
  const { t, locale } = useI18n();
  const nFormatLocale = getIntlLocaleTag(locale);
  const isAddOnly = mode === 'add_only';
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(!isAddOnly);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showAddPaymentForm, setShowAddPaymentForm] = useState(isAddOnly);
  const [showEditPayment, setShowEditPayment] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showOnlyPayments, setShowOnlyPayments] = useState(false);
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showWorkoutDetail, setShowWorkoutDetail] = useState(false);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [loadingWorkout, setLoadingWorkout] = useState(false);
  const [addCurrencySelectOpen, setAddCurrencySelectOpen] = useState(false);
  const [editCurrencySelectOpen, setEditCurrencySelectOpen] = useState(false);
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  const [isDeletingPayment, setIsDeletingPayment] = useState(false);

  // Add payment form state
  const [addPaymentForm, setAddPaymentForm] = useState({
    amount: '',
    quantity: '',
    payment_date: new Date().toLocaleDateString('en-CA'),
    comment: '',
    currency: 'RUB'
  });

  // Edit payment form state
  const [editPaymentForm, setEditPaymentForm] = useState({
    amount: '',
    quantity: '',
    payment_date: '',
    comment: '',
    currency: 'RUB'
  });

  // Inline validation errors
  const [addFormErrors, setAddFormErrors] = useState({});
  const [editFormErrors, setEditFormErrors] = useState({});

  const usedCurrencies = useMemo(() => {
    const set = new Set();
    for (const item of history) {
      if (item.currency) set.add(String(item.currency).trim().toUpperCase());
    }
    return [...set].sort();
  }, [history]);

  const [trainerPaymentCurrencies, setTrainerPaymentCurrencies] = useState([]);

  const loadTrainerPaymentCurrencies = useCallback(async () => {
    if (!trainerId) {
      setTrainerPaymentCurrencies([]);
      return;
    }
    try {
      const currOpts = { credentials: 'include' };
      const currBearer = getApiAccessToken();
      if (currBearer) currOpts.headers = { Authorization: `Bearer ${currBearer}` };
      const r = await fetch(buildApiUrl(`/trainers/${trainerId}/payment-currencies`), currOpts);
      const data = r.ok ? await r.json() : null;
      const list = Array.isArray(data?.currencies) ? data.currencies : [];
      setTrainerPaymentCurrencies(list);
    } catch {
      setTrainerPaymentCurrencies([]);
    }
  }, [trainerId]);

  useEffect(() => {
    loadTrainerPaymentCurrencies();
  }, [loadTrainerPaymentCurrencies]);

  const quickCurrenciesAdd = useMemo(
    () => buildQuickCurrencyList(trainerPaymentCurrencies, usedCurrencies, addPaymentForm.currency),
    [trainerPaymentCurrencies, usedCurrencies, addPaymentForm.currency]
  );
  const moreCurrenciesAdd = useMemo(() => {
    const inQuick = new Set(quickCurrenciesAdd);
    return CURRENCIES.filter((c) => !inQuick.has(c));
  }, [quickCurrenciesAdd]);

  const quickCurrenciesEdit = useMemo(
    () => buildQuickCurrencyList(trainerPaymentCurrencies, usedCurrencies, editPaymentForm.currency),
    [trainerPaymentCurrencies, usedCurrencies, editPaymentForm.currency]
  );
  const moreCurrenciesEdit = useMemo(() => {
    const inQuick = new Set(quickCurrenciesEdit);
    return CURRENCIES.filter((c) => !inQuick.has(c));
  }, [quickCurrenciesEdit]);

  // Load a single page of history (10 items/page)
  const loadHistoryPage = useCallback(async (pageNumber) => {
    if (!clientId) {
      return;
    }
    setLoadingHistory(true);
    try {
      const safePage = Math.max(1, parseInt(pageNumber, 10) || 1);
      const currentOffset = (safePage - 1) * PAGE_SIZE;
      const typeParam = showOnlyPayments ? '&type=payment' : '';
      const url = buildApiUrl(`/clients/${clientId}/history?limit=${PAGE_SIZE}&offset=${currentOffset}${typeParam}`);
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('HTTP error response:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      const items = Array.isArray(data.items) ? data.items : (data.items ? [data.items] : []);
      
      setHistory(items);
      setTotal(typeof data.total === 'number' ? data.total : items.length);
      setPage(safePage);
    } catch (error) {
      console.error('Error loading history:', error);
      setErrorMessage(t('payments.errorHistory', { msg: error.message }));
      setHistory([]);
      setTotal(0);
    } finally {
      setLoadingHistory(false);
    }
  }, [clientId, PAGE_SIZE, showOnlyPayments, t]);

  const loadClientData = useCallback(async (pageToLoad = 1) => {
    setLoading(true);
    setErrorMessage('');
    try {
      // Load balance
      const balFetchOpts = { credentials: 'include' };
      const balBearer = getApiAccessToken();
      if (balBearer) balFetchOpts.headers = { Authorization: `Bearer ${balBearer}` };
      const balanceResponse = await fetch(buildApiUrl(`/clients/${clientId}/balance`), balFetchOpts);
      if (!balanceResponse.ok) {
        throw new Error(`Failed to load balance: ${balanceResponse.status}`);
      }
      const balanceData = await balanceResponse.json();
      setBalance(balanceData);

      // Load initial history
      await loadHistoryPage(pageToLoad);
    } catch (error) {
      console.error('Error loading client data:', error);
      setErrorMessage(t('payments.errorLoad', { msg: error.message }));
      // Ensure history is set to empty array on error
      setHistory([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [clientId, loadHistoryPage, t]);

  useEffect(() => {
    if (isAddOnly) return;
    if (clientId) {
      loadClientData(1);
    }
  }, [clientId, loadClientData, isAddOnly]);

  useEffect(() => {
    if ((openAddPaymentOnMount || isAddOnly) && clientId) {
      setShowAddPaymentForm(true);
    }
  }, [openAddPaymentOnMount, isAddOnly, clientId]);

  // Real-time balance update: check for workouts that should be debited
  useEffect(() => {
    if (isAddOnly) return;
    if (!clientId || loading) return;

    let serverTimeOffset = 0; // Offset between client and server time
    let updateTimer = null;
    let nextCheckTime = null;

    // Get server time and calculate offset
    const syncServerTime = async () => {
      try {
        const response = await getServerTime();
        if (response && response.data && response.data.serverTime) {
          const serverTime = new Date(response.data.serverTime);
          const clientTime = new Date();
          serverTimeOffset = serverTime.getTime() - clientTime.getTime();
        } else {
          console.warn('[Balance] Invalid server time response:', response);
          // Fallback: use local time (offset = 0 means no correction)
          serverTimeOffset = 0;
        }
      } catch (error) {
        // Fallback: use client time if server time is unavailable
        // This is not critical for the app to function
        console.error('[Balance] Failed to get server time, using local device time:', error.message);
        if (error.response) {
          console.error('[Balance] Server responded with status:', error.response.status);
        } else if (error.request) {
          console.error('[Balance] No response received from server');
        }
        // Set offset to 0 to use local time
        serverTimeOffset = 0;
      }
    };

    // Get workouts and find next debit time
    const scheduleNextUpdate = async () => {
      try {
        const workoutsResponse = await getWorkouts({ client_id: clientId });
        const workouts = workoutsResponse.data || [];
        
        const currentTime = new Date(Date.now() + serverTimeOffset);
        const nextDebit = getNextDebitTime(workouts, currentTime);
        
        if (nextDebit) {
          nextCheckTime = nextDebit;
          const timeUntilDebit = nextDebit.getTime() - currentTime.getTime();
          
          if (timeUntilDebit > 0) {
            // Schedule update for when the workout should be debited
            updateTimer = setTimeout(() => {
              loadClientData(); // Reload balance
              scheduleNextUpdate(); // Schedule next check
            }, timeUntilDebit);
            
          } else {
            // Workout should already be debited, update immediately
            loadClientData();
            scheduleNextUpdate();
          }
        }
      } catch (error) {
        console.error('[Balance] Error scheduling balance update:', error);
      }
    };

    // Initial sync and schedule
    syncServerTime().then(() => {
      scheduleNextUpdate();
    });

    // Also set up a periodic check every minute as a fallback
    const periodicCheck = setInterval(() => {
      if (nextCheckTime) {
        const currentTime = new Date(Date.now() + serverTimeOffset);
        if (currentTime >= nextCheckTime) {
          loadClientData();
          scheduleNextUpdate();
        }
      }
    }, 60000); // Check every minute

    return () => {
      if (updateTimer) {
        clearTimeout(updateTimer);
      }
      clearInterval(periodicCheck);
    };
  }, [clientId, loading, loadClientData, isAddOnly]);

  // Reset and reload when filter changes
  useEffect(() => {
    if (isAddOnly) return;
    if (clientId) {
      setHistory([]);
      setTotal(0);
      setPage(1);
      loadHistoryPage(1);
    }
  }, [showOnlyPayments, clientId, loadHistoryPage, isAddOnly]);

  const validatePaymentForm = (form) => {
    const errors = {};
    const amount = parseFloat(form.amount);
    if (!form.amount || isNaN(amount) || amount <= 0 || amount < 1) {
      errors.amount = t('payments.errorAmount');
    }
    const qty = Number(form.quantity);
    if (!form.quantity || isNaN(qty) || qty <= 0 || !Number.isInteger(qty)) {
      errors.quantity = t('payments.errorQuantity');
    }
    return errors;
  };

  const handleAddPayment = async () => {
    if (isAddingPayment) return;

    const errors = validatePaymentForm(addPaymentForm);
    setAddFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsAddingPayment(true);
    try {
      const { data: result } = await createPayment({
        client_id: clientId,
        amount: parseFloat(addPaymentForm.amount),
        quantity: parseInt(addPaymentForm.quantity),
        payment_date: addPaymentForm.payment_date,
        comment: addPaymentForm.comment || null,
        currency: addPaymentForm.currency || 'RUB',
      });

      setSuccessMessage(
        result?.queued ? t('payments.successAddedOffline') : t('payments.successAdded')
      );
      setShowAddPaymentForm(false);
      setAddPaymentForm({
        amount: '',
        quantity: '',
        payment_date: new Date().toLocaleDateString('en-CA'),
        comment: '',
        currency: 'RUB'
      });
      if (!isAddOnly) {
        await loadClientData(1);
      }
      await loadTrainerPaymentCurrencies();
      onPaymentAdded?.(result);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error adding payment:', error);
      setErrorMessage(error.response?.data?.error || error.message || t('payments.errorCreate'));
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setIsAddingPayment(false);
    }
  };

  const resetAddPaymentForm = () => {
    setAddPaymentForm({
      amount: '',
      quantity: '',
      payment_date: new Date().toLocaleDateString('en-CA'),
      comment: '',
      currency: 'RUB'
    });
    setAddFormErrors({});
  };

  const handleCancelAddPayment = () => {
    if (isAddOnly) {
      onRequestClose?.();
      return;
    }
    setShowAddPaymentForm(false);
    resetAddPaymentForm();
  };

  const renderAddPaymentForm = () => (
    <div className={isAddOnly ? '' : 'mt-6 pt-6 border-t'}>
      <h3 className="text-lg font-semibold mb-4">{t('payments.addPaymentTitle')}</h3>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="amount">{t('payments.amount')}</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="1"
              value={addPaymentForm.amount}
              onChange={(e) => {
                setAddPaymentForm({ ...addPaymentForm, amount: e.target.value });
                setAddFormErrors((prev) => ({ ...prev, amount: undefined }));
              }}
              placeholder="0"
            />
            {addFormErrors.amount && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{addFormErrors.amount}</AlertDescription>
              </Alert>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="currency">{t('payments.currency')}</Label>
            <div className="flex flex-wrap gap-2" id="currency">
              {quickCurrenciesAdd.map((c) => (
                <Button
                  key={c}
                  type="button"
                  variant={addPaymentForm.currency === c ? 'default' : 'outline'}
                  size="sm"
                  className="min-h-9 min-w-[3.25rem] touch-manipulation"
                  onClick={() => setAddPaymentForm({ ...addPaymentForm, currency: c })}
                >
                  {c}
                </Button>
              ))}
            </div>
            <SelectTabBarBlockerBackdrop open={addCurrencySelectOpen} onClose={() => setAddCurrencySelectOpen(false)} />
            <Select
              open={addCurrencySelectOpen}
              onOpenChange={setAddCurrencySelectOpen}
              value={moreCurrenciesAdd.includes(addPaymentForm.currency) ? addPaymentForm.currency : undefined}
              onValueChange={(value) => setAddPaymentForm({ ...addPaymentForm, currency: value })}
            >
              <SelectTrigger className="cursor-pointer w-full">
                <SelectValue placeholder={t('payments.moreCurrencies')} />
              </SelectTrigger>
              <SelectContent className="z-[220] max-h-[min(70dvh,420px)]">
                <SelectGroup>
                  {moreCurrenciesAdd.map((currency) => (
                    <SelectItem key={currency} value={currency}>
                      {currency}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="quantity">{t('payments.quantity')}</Label>
          <Input
            id="quantity"
            type="number"
            min="1"
            step="1"
            value={addPaymentForm.quantity}
            onChange={(e) => {
              setAddPaymentForm({ ...addPaymentForm, quantity: e.target.value });
              setAddFormErrors((prev) => ({ ...prev, quantity: undefined }));
            }}
            placeholder="0"
          />
          {addFormErrors.quantity && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{addFormErrors.quantity}</AlertDescription>
            </Alert>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="payment_date">{t('payments.paymentDate')}</Label>
          <Input
            id="payment_date"
            type="date"
            value={addPaymentForm.payment_date}
            onChange={(e) => setAddPaymentForm({ ...addPaymentForm, payment_date: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="comment">{t('payments.comment')}</Label>
          <Textarea
            id="comment"
            rows={2}
            value={addPaymentForm.comment}
            onChange={(e) => setAddPaymentForm({ ...addPaymentForm, comment: e.target.value })}
            placeholder={t('payments.commentPlaceholder')}
          />
        </div>

        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
          <Button
            className="w-full sm:w-auto"
            variant="outline"
            onClick={handleCancelAddPayment}
            disabled={isAddingPayment}
          >
            {t('common.cancel')}
          </Button>
          <Button
            className="w-full sm:w-auto"
            onClick={handleAddPayment}
            disabled={isAddingPayment}
          >
            {isAddingPayment ? t('common.saving') : t('payments.addPaymentTitle')}
          </Button>
        </div>
      </div>
    </div>
  );

  const handleEditPayment = async () => {
    if (isUpdatingPayment) return;

    const errors = validatePaymentForm(editPaymentForm);
    setEditFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsUpdatingPayment(true);
    try {
      const { data } = await updatePayment(selectedPayment.id, {
        amount: parseFloat(editPaymentForm.amount),
        quantity: parseInt(editPaymentForm.quantity),
        payment_date: editPaymentForm.payment_date,
        comment: editPaymentForm.comment || null,
        currency: editPaymentForm.currency || 'RUB',
      });

      setSuccessMessage(
        data?.queued ? t('payments.successUpdatedOffline') : t('payments.successUpdated')
      );
      setShowEditPayment(false);
      setSelectedPayment(null);
      await loadClientData(page);
      await loadTrainerPaymentCurrencies();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating payment:', error);
      setErrorMessage(error.response?.data?.error || error.message || t('payments.errorUpdate'));
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setIsUpdatingPayment(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (isDeletingPayment) return;

    setIsDeletingPayment(true);
    try {
      const { data } = await deletePayment(paymentId);

      setSuccessMessage(
        data?.queued ? t('payments.successDeletedQueued') : t('payments.successDeleted')
      );
      await loadClientData(page);
      await loadTrainerPaymentCurrencies();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting payment:', error);
      setErrorMessage(error.response?.data?.error || error.message || t('payments.errorDelete'));
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setIsDeletingPayment(false);
    }
  };

  const openEditPayment = (payment) => {
    setSelectedPayment(payment);
    setEditPaymentForm({
      amount: payment.amount.toString(),
      quantity: payment.quantity.toString(),
      payment_date: payment.payment_date,
      comment: payment.comment || '',
      currency: payment.currency || 'RUB'
    });
    setEditFormErrors({});
    setShowEditPayment(true);
  };

  const formatDate = (dateString) => {
    // Check if dateString is valid
    if (!dateString || typeof dateString !== 'string') {
      return t('payments.unknownDate');
    }
    // Parse date components explicitly to avoid timezone issues
    try {
      const [year, month, day] = dateString.split('-').map(Number);
      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        return t('payments.unknownDate');
      }
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString(nFormatLocale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return t('payments.unknownDate');
    }
  };

  const getHistoryDisplayDate = (item) => {
    if (!item) return t('payments.unknownDate');
    // Payments: use payment_date
    if (item.type === 'payment') return formatDate(item.payment_date);
    // Paid deductions: use deduction_date (created from workout.date)
    if (item.type === 'deduction') return formatDate(item.deduction_date);
    // Unpaid workouts: use workout_date (or operation_date as fallback)
    if (item.type === 'unpaid') return formatDate(item.workout_date || item.operation_date);
    // Fallback
    return formatDate(item.operation_date || item.deduction_date || item.payment_date);
  };

  const formatCurrency = (amount, currency = 'RUB') => {
    return new Intl.NumberFormat(nFormatLocale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getWorkoutStatusText = (status) => {
    switch (status) {
      case 'in_progress':
        return t('payments.status.in_progress');
      case 'scheduled':
        return t('payments.status.scheduled');
      case 'completed':
        return t('payments.status.completed');
      case 'skipped':
        return t('payments.status.skipped');
      case 'cancelled':
        return t('payments.status.cancelled');
      case 'transferred':
        return t('payments.status.transferred');
      default:
        return status || t('payments.status.workout');
    }
  };

  const getWorkoutStatusBadgeVariant = (status) => {
    switch (status) {
      case 'in_progress': return 'default';
      case 'scheduled': return 'default';
      case 'completed': return 'default';
      case 'skipped': return 'destructive';
      case 'cancelled': return 'destructive';
      case 'transferred': return 'destructive';
      default: return 'outline';
    }
  };

  // Calculate workout status from workout data
  // If status is 'scheduled', recalculate based on time to ensure accuracy
  const getWorkoutStatusFromData = (item) => {
    const explicitStatus = item.workout_status || item.status;
    
    // If status is explicitly set to completed, skipped, cancelled, or transferred, use it
    if (explicitStatus && ['completed', 'skipped', 'cancelled', 'transferred'].includes(explicitStatus)) {
      return explicitStatus;
    }
    
    // If no explicit status or status is 'scheduled', calculate based on time
    if (item.workout_date && item.workout_time) {
      try {
        const now = new Date();
        // Validate workout_date before splitting
        if (!item.workout_date || typeof item.workout_date !== 'string') {
          return null;
        }
        const [year, month, day] = item.workout_date.split('-').map(Number);
        if (isNaN(year) || isNaN(month) || isNaN(day)) {
          return null;
        }
        
        // Validate workout_time before splitting
        if (!item.workout_time || typeof item.workout_time !== 'string') {
          return null;
        }
        const [hours, minutes] = item.workout_time.split(':').map(Number);
        const workoutDateTime = new Date(year, month - 1, day, hours || 0, minutes || 0, 0, 0);
        
        // Calculate end time (handle overnight: end_time < start_time means next day)
        let endDateTime;
        if (item.workout_end_time && typeof item.workout_end_time === 'string') {
          const [endHours, endMinutes] = item.workout_end_time.split(':').map(Number);
          endDateTime = new Date(year, month - 1, day, endHours || 0, endMinutes || 0, 0, 0);
          if (endDateTime <= workoutDateTime) {
            endDateTime.setDate(endDateTime.getDate() + 1);
          }
        } else {
          endDateTime = new Date(workoutDateTime.getTime() + 60 * 60 * 1000);
        }
        
        // If workout end time has passed, it's completed (unless explicitly cancelled/transferred)
        if (now > endDateTime) {
          return 'completed';
        } else if (now >= workoutDateTime && now <= endDateTime) {
          return 'in_progress';
        } else {
          return 'scheduled';
        }
      } catch (error) {
        console.error('[PaymentsTab] Error calculating workout status:', error);
        // Fallback to explicit status if calculation fails
        return explicitStatus || 'scheduled';
      }
    }
    
    // Fallback to explicit status if no date/time available
    return explicitStatus || null;
  };

  const handleOpenWorkout = async (deduction) => {
    if (!deduction.workout_id) {
      setErrorMessage(t('payments.workoutNotFound'));
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    setLoadingWorkout(true);
    try {
      // Пытаемся загрузить полные данные тренировки через API клиента
      const wFetchOpts = { credentials: 'include' };
      const wBearer = getApiAccessToken();
      if (wBearer) wFetchOpts.headers = { Authorization: `Bearer ${wBearer}` };
      const response = await fetch(buildApiUrl(`/client/${clientId}/workouts`), wFetchOpts);
      if (response.ok) {
        const workouts = await response.json();
        const workout = workouts.find(w => w.id === deduction.workout_id);
        
        if (workout) {
          setSelectedWorkout(workout);
          setShowWorkoutDetail(true);
          setLoadingWorkout(false);
          return;
        }
      }
      
      // Если не удалось найти через API, создаем объект из данных списания
      const workoutFromDeduction = {
        id: deduction.workout_id,
        date: deduction.workout_date,
        time: deduction.workout_time,
        end_time: deduction.workout_end_time,
        comment: deduction.workout_comment,
        training_type: deduction.workout_training_type,
        status: deduction.workout_status,
        client_id: clientId
      };
      setSelectedWorkout(workoutFromDeduction);
      setShowWorkoutDetail(true);
    } catch (error) {
      console.error('Error loading workout:', error);
      // Используем данные из списания как fallback
      const workoutFromDeduction = {
        id: deduction.workout_id,
        date: deduction.workout_date,
        time: deduction.workout_time,
        end_time: deduction.workout_end_time,
        comment: deduction.workout_comment,
        training_type: deduction.workout_training_type,
        status: deduction.workout_status,
        client_id: clientId
      };
      setSelectedWorkout(workoutFromDeduction);
      setShowWorkoutDetail(true);
    } finally {
      setLoadingWorkout(false);
    }
  };

  const getBalanceStatus = () => {
    if (balance === null) return { color: 'text-muted-foreground', icon: null, warning: false };
    const balanceValue = balance.balance || 0;
    if (balanceValue > 0) return { color: 'text-green-600', icon: <CheckCircle className="h-4 w-4" />, warning: false };
    if (balanceValue === 0) return { color: 'text-yellow-600', icon: <AlertTriangle className="h-4 w-4" />, warning: true };
    // Negative balance (unpaid workouts) - red color
    return { color: 'text-red-600', icon: <X className="h-4 w-4" />, warning: true };
  };

  // Filter history based on checkbox
  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));
  const pagedRangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const pagedRangeEnd = Math.min(page * PAGE_SIZE, total || 0);
  
  if (loading) {
    return (
      <div className="flex w-full min-w-0 max-w-full justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-2">{t('payments.loading')}</span>
      </div>
    );
  }

  if (isAddOnly) {
    return (
      <div className="w-full min-w-0 max-w-full space-y-4 overflow-x-hidden sm:space-y-6">
        {/* Success/Error Messages */}
        {successMessage && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        )}
        {errorMessage && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
            <X className="h-4 w-4 text-red-600" />
            <p className="text-sm text-red-800">{errorMessage}</p>
          </div>
        )}

        {renderAddPaymentForm()}
      </div>
    );
  }

  const balanceStatus = getBalanceStatus();
  const balanceValue = balance?.balance || 0;

  return (
    <div className="w-full min-w-0 max-w-full space-y-4 overflow-x-hidden sm:space-y-6">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-3">
          <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />
          <p className="min-w-0 text-sm text-green-800 break-words">{successMessage}</p>
        </div>
      )}
      {errorMessage && (
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3">
          <X className="h-4 w-4 shrink-0 text-red-600" />
          <p className="min-w-0 text-sm text-red-800 break-words">{errorMessage}</p>
        </div>
      )}

      {/* Balance Overview - Simplified */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            {t('payments.sessionBalance')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-end gap-x-3 gap-y-2 sm:items-center">
              <div className={`text-4xl sm:text-6xl leading-none font-bold tabular-nums ${balanceStatus.color}`}>
                {balanceValue < 0 ? `-${Math.abs(balanceValue)}` : balanceValue}
              </div>
              <div className="flex items-center gap-1">
                {balanceStatus.icon}
                <span className={`text-sm sm:text-base ${balanceStatus.color}`}>
                  {balanceValue > 0
                    ? t('payments.sessionsAvailable')
                    : balanceValue === 0
                      ? t('payments.sessionsNone')
                      : t('payments.sessionsUnpaid')}
                </span>
              </div>
            </div>
            <Button
              onClick={() => setShowAddPaymentForm(!showAddPaymentForm)}
              className="flex w-full items-center justify-center gap-2 sm:w-auto sm:shrink-0"
            >
              <Plus className="h-4 w-4" />
              {showAddPaymentForm ? t('payments.hideForm') : t('payments.addPayment')}
            </Button>
          </div>
          
          {/* Balance Warning */}
          {balanceStatus.warning && (
            <div className={`mt-4 p-3 sm:p-4 rounded-md border ${
              balanceValue === 0 
                ? 'bg-yellow-50 border-yellow-200 text-yellow-800' 
                : 'bg-red-50 border-red-200 text-red-800'
            }`}>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-medium">
                  {balanceValue === 0 ? t('payments.balanceDepleted') : t('payments.balanceNegative')}
                </span>
              </div>
              {balanceValue !== 0 && (
                <p className="text-sm mt-1">
                  {t('payments.balanceDebtHint')}
                </p>
              )}
            </div>
          )}

          {/* Add Payment Form - Inline */}
          {showAddPaymentForm && renderAddPaymentForm()}
        </CardContent>
      </Card>

      {/* History of Payments and Deductions */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="leading-tight">{t('payments.historyTitle')}</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Checkbox
                checked={showOnlyPayments}
                onCheckedChange={(v) => setShowOnlyPayments(v === true)}
                id="show-only-payments"
              />
              <Label htmlFor="show-only-payments" className="text-sm font-normal cursor-pointer">
                {t('payments.showPaymentsOnly')}
              </Label>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {showOnlyPayments ? t('payments.noPaymentRecords') : t('payments.noEntries')}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((item) => (
                <Card key={`${item.type}-${item.id}`} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-3 sm:p-4">
                    {item.type === 'payment' ? (
                      // Payment display
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-3 mb-2">
                            <Badge variant="outline" className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(item.payment_date)}
                            </Badge>
                            <span className="font-semibold text-green-600">
                              {t('payments.sessionsAdded', { n: item.quantity })}
                            </span>
                            <span className="font-semibold">
                              {formatCurrency(item.amount, item.currency || 'RUB')}
                            </span>
                          </div>
                          {item.comment && (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground mb-1">
                              <MessageSquare className="h-3 w-3" />
                              {item.comment}
                            </div>
                          )}
                          <div className="text-sm text-muted-foreground">
                            {t('payments.balanceAfter')}{' '}
                            <span className="font-medium">{item.balance_after}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditPayment(item)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{t('payments.deletePaymentTitle')}</AlertDialogTitle>
                                <AlertDialogDescription>
                                  {t('payments.deletePaymentConfirm')}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeletePayment(item.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  disabled={isDeletingPayment}
                                >
                                  {isDeletingPayment ? t('common.saving') : t('common.delete')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    ) : (
                      // Deduction / Unpaid display - clickable to open workout (if workout_id exists)
                      <div 
                        className={`flex items-start justify-between ${item.workout_id ? 'cursor-pointer hover:bg-muted/50 transition-colors rounded-md p-2 -m-2' : ''}`}
                        onClick={() => item.workout_id && !loadingWorkout && handleOpenWorkout(item)}
                        title={item.workout_id ? t('payments.openWorkout') : ''}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <Badge variant="outline" className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {getHistoryDisplayDate(item)}
                            </Badge>
                            <span className="font-semibold text-red-600">
                              {t('payments.oneSessionDeducted')}
                            </span>
                            {item.workout_id && (() => {
                              const workoutStatus = getWorkoutStatusFromData(item);
                              return workoutStatus ? (
                                <Badge 
                                  variant={getWorkoutStatusBadgeVariant(workoutStatus)} 
                                  className="flex items-center gap-1 text-xs"
                                >
                                  {getWorkoutStatusText(workoutStatus)}
                                </Badge>
                              ) : null;
                            })()}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {t('payments.balanceAfter')}{' '}
                            <span className="font-medium">{item.balance_after ?? '—'}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Pagination controls (moved to bottom) */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-4">
            <div className="text-sm text-muted-foreground">
              {total > 0 ? (
                <>
                  {t('payments.shownRange', {
                    from: pagedRangeStart,
                    to: pagedRangeEnd,
                    total,
                  })}{' '}
                  <span className="mx-2">•</span>
                  {t('payments.pageOf', { page, pages: totalPages })}
                </>
              ) : (
                <>{t('payments.pageOne')}</>
              )}
            </div>
            <div className="flex w-full items-center gap-2 sm:w-auto sm:justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={loading || loadingHistory || page <= 1}
                onClick={() => loadHistoryPage(page - 1)}
              >
                {t('payments.back')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={loading || loadingHistory || page >= totalPages}
                onClick={() => loadHistoryPage(page + 1)}
              >
                {t('payments.forward')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>


      {/* Edit Payment Dialog */}
      <Dialog open={showEditPayment} onOpenChange={setShowEditPayment}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('payments.editPayment')}</DialogTitle>
            <DialogDescription>
              {t('payments.editPaymentDesc')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-amount">{t('payments.amount')}</Label>
                <Input
                  id="edit-amount"
                  type="number"
                  step="0.01"
                  min="1"
                  value={editPaymentForm.amount}
                  onChange={(e) => {
                    setEditPaymentForm({...editPaymentForm, amount: e.target.value});
                    setEditFormErrors((prev) => ({ ...prev, amount: undefined }));
                  }}
                />
                {editFormErrors.amount && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{editFormErrors.amount}</AlertDescription>
                  </Alert>
                )}
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="edit-currency">{t('payments.currency')}</Label>
                <div className="flex flex-wrap gap-2" id="edit-currency">
                  {quickCurrenciesEdit.map((c) => (
                    <Button
                      key={c}
                      type="button"
                      variant={editPaymentForm.currency === c ? 'default' : 'outline'}
                      size="sm"
                      className="min-h-9 min-w-[3.25rem] touch-manipulation"
                      onClick={() => setEditPaymentForm({ ...editPaymentForm, currency: c })}
                    >
                      {c}
                    </Button>
                  ))}
                </div>
                <SelectTabBarBlockerBackdrop open={editCurrencySelectOpen} onClose={() => setEditCurrencySelectOpen(false)} />
                <Select
                  open={editCurrencySelectOpen}
                  onOpenChange={setEditCurrencySelectOpen}
                  value={moreCurrenciesEdit.includes(editPaymentForm.currency) ? editPaymentForm.currency : undefined}
                  onValueChange={(value) => setEditPaymentForm({ ...editPaymentForm, currency: value })}
                >
                  <SelectTrigger className="cursor-pointer w-full">
                    <SelectValue placeholder={t('payments.moreCurrencies')} />
                  </SelectTrigger>
                  <SelectContent className="z-[220] max-h-[min(70dvh,420px)]">
                    <SelectGroup>
                      {moreCurrenciesEdit.map((currency) => (
                        <SelectItem key={currency} value={currency}>
                          {currency}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-quantity">{t('payments.quantity')}</Label>
              <Input
                id="edit-quantity"
                type="number"
                min="1"
                step="1"
                value={editPaymentForm.quantity}
                onChange={(e) => {
                  setEditPaymentForm({...editPaymentForm, quantity: e.target.value});
                  setEditFormErrors((prev) => ({ ...prev, quantity: undefined }));
                }}
              />
              {editFormErrors.quantity && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{editFormErrors.quantity}</AlertDescription>
                </Alert>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-payment_date">{t('payments.paymentDate')}</Label>
              <Input
                id="edit-payment_date"
                type="date"
                value={editPaymentForm.payment_date}
                onChange={(e) => setEditPaymentForm({...editPaymentForm, payment_date: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-comment">{t('payments.comment')}</Label>
              <Textarea
                id="edit-comment"
                rows={2}
                value={editPaymentForm.comment}
                onChange={(e) => setEditPaymentForm({...editPaymentForm, comment: e.target.value})}
                placeholder={t('payments.commentPlaceholder')}
              />
            </div>

            <div className="flex flex-col-reverse gap-2 pt-4 sm:flex-row sm:justify-end">
              <Button
                className="w-full sm:w-auto"
                variant="outline"
                onClick={() => setShowEditPayment(false)}
                disabled={isUpdatingPayment}
              >
                {t('common.cancel')}
              </Button>
              <Button className="w-full sm:w-auto" onClick={handleEditPayment} disabled={isUpdatingPayment}>
                {isUpdatingPayment ? t('common.saving') : t('payments.saveChanges')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Workout Detail Modal */}
      <WorkoutDetailModal
        show={showWorkoutDetail}
        onHide={() => {
          setShowWorkoutDetail(false);
          setSelectedWorkout(null);
        }}
        workout={selectedWorkout}
        trainerId={trainerId}
        onWorkoutUpdate={loadClientData}
      />
    </div>
  );
};

export default PaymentsTab;
